<script setup lang="ts">
/**
 * The lintel — the same header on the landing page and on the news pages,
 * because a header that changes shape depending on which of those a
 * visitor is on is what makes the second one feel like a different site
 * bolted on rather than a page of this one.
 *
 * Fully self-contained: the drawer's own open state and its Escape-to-close
 * both live here rather than in whichever page mounts it, so nothing about
 * mounting this component depends on what else that page's script does.
 * `NAV`'s links use `goToSiteSection` rather than a local scroll function —
 * see that module for why one link has to mean two different things
 * depending on where it was clicked from.
 *
 * `scrolled` is owned by the page that mounts this — `LandingScreen.vue`
 * derives it from an `IntersectionObserver` on the hero's own logo — and is
 * `false` by default (undefined pages, e.g. the news routes, get the bare
 * lintel forever, which is the right degradation for a header nobody scrolls
 * a hero out from under). This component only turns the one boolean into a
 * class; see `landing.css`'s Lintel section for what that class actually
 * animates and why the logo and the background share it rather than each
 * carrying its own threshold.
 *
 * `menuOpen` becomes a class on the same element for one reason worth stating:
 * the drawer has no background of its own and the bar is transparent until
 * `scrolled`, so opening the burger at the top of the page painted the nav
 * links straight onto the hero. `lintel-menu` makes the bar solid for as long
 * as the drawer is open, whatever the scroll position. It also matters more
 * than it used to — below 64 rem the landing page's bar is `fixed` and bare at
 * rest, floating only the burger and the music toggle over the hero, so there
 * is nothing else for the drawer to sit on.
 */
import { onBeforeUnmount, onMounted, ref } from 'vue';

import BrandLogo from '../ui/BrandLogo.vue';
import SpeakerIcon from '../ui/SpeakerIcon.vue';
import { navigateNews } from '../state/news-route';
import { NAV } from './landing-content';
import { goToSiteSection } from './site-nav';
import { musicMuted } from '../state/settings';

withDefaults(defineProps<{ scrolled?: boolean }>(), { scrolled: false });

const emit = defineEmits<{ play: [] }>();

const menuOpen = ref(false);

function goTo(href: string): void {
  menuOpen.value = false;
  goToSiteSection(href);
}

function onKey(event: KeyboardEvent): void {
  if (event.key === 'Escape' && menuOpen.value) menuOpen.value = false;
}

onMounted(() => window.addEventListener('keydown', onKey));
onBeforeUnmount(() => window.removeEventListener('keydown', onKey));
</script>

<template>
  <header class="lintel" :class="{ 'lintel-scrolled': scrolled, 'lintel-menu': menuOpen }">
    <div class="lintel-in">
      <a class="brand" href="#top" @click.prevent="goTo('#top')">
        <BrandLogo />
      </a>

      <nav class="lintel-nav" aria-label="Sections">
        <a
          v-for="item in NAV"
          :key="item.href"
          :href="item.href"
          @click.prevent="goTo(item.href)"
          >{{ item.label }}</a
        >
        <a href="/news" @click.prevent="navigateNews('/news')">News</a>
      </nav>

      <div class="lintel-cta">
        <button
          type="button"
          class="music-toggle"
          :class="{ muted: musicMuted }"
          :aria-pressed="!musicMuted"
          :aria-label="musicMuted ? 'Unmute music' : 'Mute music'"
          :title="musicMuted ? 'Unmute music' : 'Mute music'"
          @click="musicMuted = !musicMuted"
        >
          <SpeakerIcon :muted="musicMuted" />
        </button>
        <button type="button" class="btn btn-solid btn-sm" @click="emit('play')">
          Play free
        </button>
        <button
          type="button"
          class="burger"
          :aria-expanded="menuOpen"
          aria-controls="landing-drawer"
          aria-label="Sections"
          @click="menuOpen = !menuOpen"
        >
          <span></span><span></span><span></span>
        </button>
      </div>
    </div>

    <nav id="landing-drawer" class="drawer" :class="{ open: menuOpen }" aria-label="Sections">
      <button type="button" class="drawer-cta" @click="emit('play')">Play free</button>
      <button
        type="button"
        class="drawer-music"
        :aria-pressed="!musicMuted"
        @click="musicMuted = !musicMuted"
      >
        <SpeakerIcon :muted="musicMuted" />
        {{ musicMuted ? 'Unmute music' : 'Mute music' }}
      </button>
      <a
        v-for="item in NAV"
        :key="item.href"
        :href="item.href"
        @click.prevent="goTo(item.href)"
        >{{ item.label }}</a
      >
      <a href="/news" @click.prevent="navigateNews('/news')">News</a>
    </nav>
  </header>
</template>
